import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { motion } from "framer-motion";
import { FiDownload, FiX, FiCheckCircle } from "react-icons/fi";

const inputClass =
  "w-full py-3 px-3 border border-slate-300 rounded-md text-[13px] text-[#415674] outline-none focus:border-[#F86624] focus:ring-1 focus:ring-[#F86624]/30 transition-colors bg-white";

const labelClass = "block text-sm font-medium text-[#4c4d56] mb-1";

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  scope: Yup.string().trim().required("Scope of business is required"),
});

/**
 * Lead-capture modal shown before a resource download.
 * Submits to the existing shortForm API, then reveals the Download button.
 */
const DownloadLeadForm = ({ resource, onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (values) => {
    setError("");
    try {
      await axios.post("https://api.mbasolutions.co/shortForm", {
        name: values.name,
        email: values.email,
        subject: `Download Request: ${resource.title}`,
        message: [
          `Resource: ${resource.title}`,
          `Scope of Business: ${values.scope}`,
        ].join("\n"),
        requestCall: false,
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[20001] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-form-title"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6 md:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
        >
          <FiX size={20} />
        </button>

        <span className="text-xs font-semibold text-[var(--bg-primary)]">
          {resource.category}
        </span>
        <h2
          id="download-form-title"
          className="text-xl font-bold text-gray-900 mt-1 mb-2 pr-6"
        >
          {resource.title}
        </h2>

        {submitted ? (
          <div className="text-center pt-4">
            <FiCheckCircle
              size={44}
              className="mx-auto text-[#F86624] mb-3"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-sm mb-6">
              Thank you! Your resource is ready to download.
            </p>
            <a
              href={resource.file}
              download
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              <FiDownload size={16} /> Download
            </a>
          </div>
        ) : (
          <>
            <p className="text-gray-600 text-sm mb-5">
              Please share a few details to access this resource.
            </p>
            <Formik
              initialValues={{ name: "", email: "", scope: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-4" noValidate>
                  <div>
                    <label htmlFor="download-name" className={labelClass}>
                      Name <span className="text-[#F86624]">*</span>
                    </label>
                    <Field
                      id="download-name"
                      name="name"
                      type="text"
                      className={inputClass}
                      placeholder="Your full name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-red-600 text-[13px] mt-1"
                    />
                  </div>
                  <div>
                    <label htmlFor="download-email" className={labelClass}>
                      Email <span className="text-[#F86624]">*</span>
                    </label>
                    <Field
                      id="download-email"
                      name="email"
                      type="email"
                      className={inputClass}
                      placeholder="you@company.com"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-600 text-[13px] mt-1"
                    />
                  </div>
                  <div>
                    <label htmlFor="download-scope" className={labelClass}>
                      Scope of Business <span className="text-[#F86624]">*</span>
                    </label>
                    <Field
                      id="download-scope"
                      name="scope"
                      as="textarea"
                      rows={3}
                      className={`${inputClass} resize-none`}
                      placeholder="e.g. Food processing, textile manufacturing, IT services"
                    />
                    <ErrorMessage
                      name="scope"
                      component="div"
                      className="text-red-600 text-[13px] mt-1"
                    />
                  </div>
                  {error && (
                    <div className="text-red-600 text-[13px]" role="alert">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 rounded-full bg-[#F86624] hover:bg-[#D9531A] text-white font-semibold transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </Form>
              )}
            </Formik>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default DownloadLeadForm;
